import React from 'react';
import OakStatusBadge from './compliance/OakStatusBadge';
import { isEcoStage, ecoStageToFrame, ecoStageLabel } from '../lib/ecoAward';

// Compact pill version of the badge — for use in supplier list rows,
// brief comparison column footers, anywhere the full hex shield is too
// big. Renders a mini 12px SVG of the hex (gradient-filled, no leaves
// or chevrons — at this size the detail wouldn't read) + the tier
// label inline.
//
// Usage:
//   <BadgeChip tier="silver" category="Eco" />
//   → renders a pill saying "Eco · Silver" with a tiny silver hex left
//
// Each chip generates its own gradient id so multiple chips on the
// same page don't collide on gradient references.

const TIER_TONE = {
  bronze: { bg: 'rgba(205, 127, 50, 0.1)',  border: 'rgba(205, 127, 50, 0.4)',  text: '#CD7F32', stops: ['#E89D5C', '#CD7F32', '#8B5A1F'] },
  silver: { bg: 'rgba(192, 192, 192, 0.08)', border: 'rgba(192, 192, 192, 0.4)', text: '#C0C0C0', stops: ['#E8E8E8', '#C0C0C0', '#8A8A8A'] },
  gold:   { bg: 'rgba(229, 185, 65, 0.1)',  border: 'rgba(229, 185, 65, 0.4)',  text: '#E5B941', stops: ['#F4D26B', '#E5B941', '#A88323'] },
};

let chipIdCounter = 0;
const nextChipId = () => `chip-${++chipIdCounter}`;

// Eco chip tone — Fusion Green, matching the growing-oak award (no metal).
const ECO_TONE = { bg: 'rgba(146, 212, 0, 0.10)', border: 'rgba(146, 212, 0, 0.4)', text: '#92D400' };

export default function BadgeChip({ tier = 'bronze', category = 'Eco', expired = false }) {
  // Eco is the growing-oak award: `tier` carries an oak stage, not a metal.
  // Show a mini oak + stage label instead of the hex + Bronze/Silver/Gold.
  const isEco = isEcoStage(tier);
  const tone = isEco ? ECO_TONE : (TIER_TONE[tier] || TIER_TONE.bronze);
  const gradId = React.useMemo(nextChipId, []);
  const tierLabel = isEco ? ecoStageLabel(tier) : tier.charAt(0).toUpperCase() + tier.slice(1);
  const opacity = expired ? 0.45 : 1;

  return (
    <span
      className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border font-['Metropolis'] text-[10px] font-bold tracking-[0.15em] uppercase whitespace-nowrap"
      style={{
        background: tone.bg,
        borderColor: tone.border,
        color: tone.text,
        opacity,
      }}>
      {isEco ? (
        <span className="inline-flex" aria-hidden="true">
          <OakStatusBadge frame={ecoStageToFrame(tier)} size={12} animate={false} />
        </span>
      ) : (
        <svg width="12" height="14" viewBox="0 0 120 138" aria-hidden="true">
          <defs>
            <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="100%">
              {tone.stops.map((c, i) => (
                <stop key={i} offset={`${i * 50}%`} stopColor={c} />
              ))}
            </linearGradient>
          </defs>
          <path
            d="M 60 4 L 110 22 L 110 78 L 60 134 L 10 78 L 10 22 Z"
            fill={`url(#${gradId})`}
            stroke={tone.text}
            strokeWidth="3" />
        </svg>
      )}
      <span>{category} · {tierLabel}</span>
    </span>
  );
}
